import React, { useState, useEffect, useRef } from 'react';
import { VStack, HStack, Box, Text, Textarea, Link, Button, Image, Icon } from '@chakra-ui/react';
import type { ReceiptData } from '@kuow/core';
import { submitDonationComment, submitDoubleTheDonation } from '../DonationForm/services/donationSubmission';
import { useDoubleTheDonation } from '../DoubleTheDonation/useDoubleTheDonation';
import { DTD_CONTAINER_ID } from '../DoubleTheDonation/loadDtdScript';
import { logger, donateEvent } from '@kuow/core';
import { LuEqual, LuPlus } from 'react-icons/lu';
import type { DonationFormMode } from '../DonationForm/types';
import FacebookIcon from '../../assets/images/facebook-icon.svg';
import XIcon from '../../assets/images/twitter-icon.svg';
import LinkedInIcon from '../../assets/images/linkedin-icon.svg';
import EmailIcon from '../../assets/images/email-icon.svg';
import GiftIcon from '../../assets/images/gift-icon.svg';
import GiftIconLarge from '../../assets/images/gift-icon-large.svg';
import MailIcon from '../../assets/images/mail-icon.svg';
import SearchIcon from '../../assets/images/search-icon.svg';
import ChevronDownIcon from '../../assets/images/chevron-down-icon.svg';
import PaperTexture from '../../assets/images/paper-texture.jpg';

/**
 * Detects if an element is inside a Shadow DOM and returns the shadow root
 */
function getShadowRoot(element: Element | null): ShadowRoot | null {
  if (!element) return null;
  const root = element.getRootNode();
  return root instanceof ShadowRoot ? root : null;
}

export type ReceiptMode = DonationFormMode | 'standalone';

export interface ReceiptProps {
  receiptData: ReceiptData;
  mode?: ReceiptMode;
  hadActiveChallenge?: boolean;
  /** GA4 donatePlacement for receipt events; omitted on standalone receipts. */
  placement?: string | null;
}

// Generate SVG mask data URL for receipt shape with torn edges
const generateReceiptMask = (): string => {
  // Create path that follows the receipt outline with jagged top and bottom edges
  // The mask defines the visible area (white = visible, black/transparent = hidden)
  // Top jagged edge - follows the torn edge pattern
  let path = 'M 0,7';
  for (let i = 0; i < 20; i++) {
    const x = i * 12;
    // Top pattern: goes from y=7 up to y=0, then back to y=7
    path += ` L ${x + 6},0 L ${x + 12},7`;
  }
  // Right side (straight)
  path += ' L 240,7 L 240,192';
  // Bottom jagged edge - follows the torn edge pattern in reverse
  for (let i = 19; i >= 0; i--) {
    const x = i * 12;
    // Bottom pattern: goes from y=192 down to y=200, then back to y=192
    path += ` L ${x + 12},192 L ${x + 6},200 L ${x},192`;
  }
  // Left side and close back to start
  path += ' L 0,192 Z';
  
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="200">
    <rect width="240" height="200" fill="var(--chakra-colors-bg-panel)"/>
    <path d="${path}" fill="var(--chakra-colors-fg-default)"/>
  </svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// Torn paper edge component using SVG triangles
const TornEdge: React.FC<{ top?: boolean }> = ({ top = true }) => {
  // Create 20 triangles for the torn edge
  // Swapped: top uses bottom pattern, bottom uses top pattern
  const triangles = Array.from({ length: 20 }, (_, i) => (
    <polygon
      key={i}
      points={top ? "0,0 6,7 12,0" : "0,7 6,0 12,7"}
      fill="var(--chakra-colors-bg-panel)"
      transform={`translate(${i * 12}, 0)`}
    />
  ));

  return (
    <Box
      position="absolute"
      left={0}
      right={0}
      height="8px"
      overflow="visible"
      {...(top ? { top: '0' } : { bottom: '-1px' })}
      zIndex={2}
    >
      <svg
        width="240"
        height="8"
        viewBox="0 0 240 8"
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%' }}
      >
        {triangles}
      </svg>
    </Box>
  );
};

export const Receipt: React.FC<ReceiptProps> = ({ receiptData, mode = 'default', hadActiveChallenge = false, placement = null }) => {
  const [testimonialText, setTestimonialText] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [commentSubmitted, setCommentSubmitted] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(new Set());
  const [isSubmittingDtd, setIsSubmittingDtd] = useState(false);
  const [dtdSubmitted, setDtdSubmitted] = useState(false);
  const maxCharacters = 200;

  // Ref and state for shadow DOM detection
  const containerRef = useRef<HTMLDivElement>(null);
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null);
  
  // Ref and state for detecting if receipt and gift image are wrapping
  const receiptStackRef = useRef<HTMLDivElement>(null);
  const [isWrapped, setIsWrapped] = useState(false);

  // Detect shadow root on mount
  useEffect(() => {
    if (containerRef.current) {
      const sr = getShadowRoot(containerRef.current);
      if (sr) {
        logger.info('Receipt: Running in Shadow DOM mode');
        setShadowRoot(sr);
      }
    }
  }, []);

  // Detect if receipt and gift image should switch to stacked layout
  const giftImageUrl = receiptData.giftImageUrl ?? undefined;
  const shouldShowGift = Boolean(giftImageUrl && mode !== 'billing-update');

  useEffect(() => {
    if (!receiptStackRef.current || !shouldShowGift) {
      setIsWrapped(false);
      return;
    }

    const checkWrapping = () => {
      const stack = receiptStackRef.current;
      if (!stack) return;

      const children = Array.from(stack.children);
      if (children.length < 2) {
        setIsWrapped(false);
        return;
      }

      const receiptBox = children[0] as HTMLElement;
      if (!receiptBox) {
        setIsWrapped(false);
        return;
      }

      const stackWidth = stack.getBoundingClientRect().width;
      const receiptWidth = receiptBox.getBoundingClientRect().width;
      const remainingSpace = stackWidth - receiptWidth;

      setIsWrapped(remainingSpace < 366);
    };

    checkWrapping();

    const resizeObserver = new ResizeObserver(checkWrapping);
    resizeObserver.observe(receiptStackRef.current);

    return () => resizeObserver.disconnect();
  }, [shouldShowGift]);

  // Generate share URLs based on donation form URL
  const shareUrl = receiptData.formUrl;
  const shareMessage = 'I just supported KUOW! Join me in keeping quality journalism alive.';
  const emailSubject = 'Support KUOW';
  const emailBody = `${shareMessage}\n\n`;
  const hasReceiptEmail = receiptData.email?.trim().length > 0;
  
  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}&url=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    email: `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody + shareUrl)}`,
  };

  // Initialize Double the Donation with shadow root support
  const { company: dtdCompany, isLoaded: isDtdLoaded } = useDoubleTheDonation({ shadowRoot });

  // Minimal DTD input styling: match button height + corner radius (keep everything else plugin-default)
  useEffect(() => {
    if (!isDtdLoaded) return;

    const root = shadowRoot ?? document;
    const container = root.getElementById(DTD_CONTAINER_ID);
    if (!container) return;

    let isApplying = false;

    const applySizing = () => {
      if (isApplying) return;
      isApplying = true;
      try {
      // Inject a real node for the icon so it is visible in Chrome Elements.
      // Anchor it to the actual input's parent (more reliable than placeholder nodes).
      const inputEl = container.querySelector('.dtd-search-input, input');
      const inputParent = inputEl?.parentElement as HTMLElement | null;
      const ICON_ID = 'kuow-dtd-search-icon';
      let iconEl: HTMLElement | null = null;

      if (inputParent) {
        inputParent.style.setProperty('position', 'relative', 'important');

        // Cleanup any older debug node ids if present
        inputParent.querySelector('#kuow-dtd-search-icon-debug')?.remove();

        iconEl = inputParent.querySelector(`#${ICON_ID}`);
        if (!iconEl) {
          iconEl = document.createElement('span');
          iconEl.id = ICON_ID;
          inputParent.prepend(iconEl);
        }

        iconEl.style.setProperty('position', 'absolute', 'important');
        iconEl.style.setProperty('left', '12px', 'important');
        iconEl.style.setProperty('top', '50%', 'important');
        iconEl.style.setProperty('width', '16px', 'important');
        iconEl.style.setProperty('height', '16px', 'important');
        iconEl.style.setProperty('transform', 'translateY(-50%)', 'important');
        iconEl.style.setProperty('pointer-events', 'none', 'important');
        iconEl.style.setProperty('z-index', '3', 'important');

        // Render the icon using mask so it inherits theme color.
        iconEl.style.setProperty('background-color', 'var(--chakra-colors-gray-fg)', 'important');
        iconEl.style.setProperty('-webkit-mask', `url("${SearchIcon}") no-repeat center / contain`, 'important');
        iconEl.style.setProperty('mask', `url("${SearchIcon}") no-repeat center / contain`, 'important');
      }

      const searchBox = container.querySelector('.dtd-search-box');
      if (searchBox) {
        const el = searchBox as HTMLElement;
        el.style.setProperty('background', 'transparent', 'important');
        el.style.setProperty('background-color', 'transparent', 'important');
        // Allow the autocomplete results to render outside the box (plugin sometimes sets fixed height/overflow)
        el.style.setProperty('overflow', 'visible', 'important');
        el.style.setProperty('height', 'auto', 'important');
        el.style.setProperty('min-height', '40px', 'important');
      }

      // Style the "Search for company" label 2px smaller than default
      const label = container.querySelector('label');
      if (label) {
        const el = label as HTMLElement;
        el.style.setProperty('font-size', '12px', 'important');
        el.style.setProperty('line-height', '16px', 'important');
      }

      // Force placeholder font size (input placeholder lives in UA shadow root)
      const placeholderStyleId = 'kuow-dtd-placeholder-styles';
      const styleHost =
        root instanceof ShadowRoot ? root : root.head ?? root.documentElement ?? root.body;
      const rootDocument = root instanceof ShadowRoot ? root.host.ownerDocument : root;
      let placeholderStyleEl = root.querySelector<HTMLStyleElement>(`#${placeholderStyleId}`);
      if (!placeholderStyleEl) {
        placeholderStyleEl = rootDocument.createElement('style');
        placeholderStyleEl.id = placeholderStyleId;
        styleHost?.appendChild(placeholderStyleEl);
      }
      placeholderStyleEl.textContent = `
        #${DTD_CONTAINER_ID} .dtd-search-input::placeholder,
        #${DTD_CONTAINER_ID} input::placeholder {
          font-size: 14px !important;
          line-height: 20px !important;
        }
      `;

      const searchInput = container.querySelector('.dtd-search-input');
      if (searchInput) {
        const el = searchInput as HTMLElement;
        const shouldHideIcon =
          dtdCompany !== null ||
          Boolean(
            container.querySelector(
              '.dtd-autocomplete-results, .dtd-results, ul[role="listbox"], [role="listbox"]',
            ),
          );

        el.style.setProperty('height', '40px', 'important');
        el.style.setProperty('border-radius', 'var(--chakra-radii-l2, 6px)', 'important');
        el.style.setProperty('padding-left', shouldHideIcon ? '12px' : '40px', 'important');
        el.style.setProperty('box-sizing', 'border-box', 'important');

        if (iconEl) {
          iconEl.style.setProperty('display', shouldHideIcon ? 'none' : 'block', 'important');
        }
      } else {
        const input = container.querySelector('input');
        if (input) {
          const el = input as HTMLElement;
          el.style.setProperty('height', '40px', 'important');
          el.style.setProperty('padding-left', '40px', 'important');
          el.style.setProperty('box-sizing', 'border-box', 'important');
        }
      }

      const companySelected = container.querySelector('.dtd-company-selected');
      if (companySelected) {
        const el = companySelected as HTMLElement;
        el.style.setProperty('display', 'block', 'important');
        // Chakra "xs" is 12px in our system.
        el.style.setProperty('font-size', '12px', 'important');
        el.style.setProperty('line-height', '16px', 'important');
      }

      const wrongCompany = container.querySelector('.wrongcompany');
      if (wrongCompany) {
        const el = wrongCompany as HTMLElement;
        el.style.setProperty('display', 'block', 'important');
        el.style.setProperty('font-style', 'italic', 'important');
        // Keep this small to match the selected-company message.
        el.style.setProperty('font-size', '12px', 'important');
        el.style.setProperty('line-height', '16px', 'important');
      }

      const companySelectedNames = container.querySelectorAll('.dtd-company-selected-name');
      if (companySelectedNames.length > 0) {
        const doc = container.ownerDocument;

        companySelectedNames.forEach((node) => {
          const el = node as HTMLElement;

          // Ensure a single <br> exists immediately before the name.
          const prev = el.previousSibling;
          const hasImmediateBr =
            prev instanceof HTMLElement && prev.tagName.toLowerCase() === 'br';
          if (!hasImmediateBr) {
            const br = doc.createElement('br');
            el.parentNode?.insertBefore(br, el);
          }

          // Medium font size for the selected company name.
          el.style.setProperty('font-size', '14px', 'important');
          el.style.setProperty('line-height', '20px', 'important');
        });
      }

      } finally {
        isApplying = false;
      }
    };

    applySizing();
    const observer = new MutationObserver(() => applySizing());
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      root.querySelector<HTMLStyleElement>('#kuow-dtd-placeholder-styles')?.remove();
    };
  }, [dtdCompany, isDtdLoaded, shadowRoot]);

  const toggleAccordion = (id: string) => {
    setExpandedAccordions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;

  // Handle comment submission
  const handleCommentSubmit = async () => {
    if (!testimonialText.trim()) {
      return;
    }

    if (testimonialText.trim().length > 5) {
      donateEvent('Comment', placement);
    }

    setIsSubmittingComment(true);

    try {
      const result = await submitDonationComment(
        receiptData.donationId,
        receiptData.receiptId,
        testimonialText
      );

      if (result.success) {
        setCommentSubmitted(true);
        logger.info('Comment submitted successfully', {
          donationId: receiptData.donationId,
        });
      } else {
        logger.error('Failed to submit comment', { error: result.error });
        // TODO: Show error message to user
      }
    } catch (error) {
      logger.error('Error submitting comment', { error });
      // TODO: Show error message to user
    } finally {
      setIsSubmittingComment(false);
    }
  };

  // Handle DTD submission
  const handleDtdSubmit = async () => {
    if (!dtdCompany) {
      return;
    }

    donateEvent('Employer Match', placement);
    setIsSubmittingDtd(true);

    try {
      const result = await submitDoubleTheDonation(
        receiptData.donationId,
        receiptData.receiptId,
        dtdCompany
      );

      if (result.success) {
        setDtdSubmitted(true);
        logger.info('DTD info submitted successfully', {
          donationId: receiptData.donationId,
          companyName: dtdCompany.name,
        });
      } else {
        logger.error('Failed to submit DTD info', { error: result.error });
        // TODO: Show error message to user
      }
    } catch (error) {
      logger.error('Error submitting DTD info', { error });
      // TODO: Show error message to user
    } finally {
      setIsSubmittingDtd(false);
    }
  };

  return (
    <VStack
      ref={containerRef}
      gap={4}
      align="stretch"
      w="full"
      maxW="400px"
      mx="auto"
    >
      {/* A. Header */}
      <VStack gap={2} textAlign="center" pb={3}>
        <Text fontSize="xl" fontWeight="bold">
          Thank You!
        </Text>

        {/* Show matching challenge message if:
        - In default mode
        - In monthly-upgrade mode and had active challenge
        - NOT in billing-update mode
        - NOT in standalone mode (receipt permalink page)
        - NOT billing-update where originally had an active challenge, but now doesn't
        */}
        {receiptData.matchingChallengeDonation &&
        (mode === 'default' || (mode === 'monthly-upgrade' && hadActiveChallenge)) ? (
          <Text fontSize="sm" fontWeight="semibold" color="fg">
            Your gift was matched!
          </Text>
        ) : null}

        {hasReceiptEmail ? (
          <VStack gap={0}>
            <Text fontSize="xs" fontStyle="italic" color="fg.muted">
              Your receipt has been sent to:
            </Text>
            <Text fontSize="xs" fontWeight="semibold" fontStyle="italic" color="fg.default">
              {receiptData.email}
            </Text>
          </VStack>
        ) : null}
        </VStack>

      {/* B. Receipt + Share */}
      <VStack gap={3.5} align="stretch" w="full">
        <HStack ref={receiptStackRef} gap={0} align="center" justify="center" flexWrap="wrap" w="full">
          {/* Receipt Details */}
          <Box
            position="relative"
            bg="gray.50"
            flex={shouldShowGift ? '1' : undefined}
            minW="176px"
            maxW="200px"
            transform="rotate(-2.4deg)"
            zIndex={1}
            overflow="visible"
          >
            {/* Texture layer positioned absolutely behind, masked to receipt shape with torn edges */}
            <Box
              position="absolute"
              top="0"
              left={0}
              right={0}
              bottom="0"
              backgroundImage={`url(${PaperTexture})`}
              backgroundRepeat="no-repeat"
              backgroundSize="cover"
              backgroundBlendMode="multiply"
              css={{
                maskImage: generateReceiptMask(),
                WebkitMaskImage: generateReceiptMask(),
                maskSize: '100% 100%',
                WebkitMaskSize: '100% 100%',
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
              }}
              zIndex={0}
              pointerEvents="none"
            />
            {/* Torn paper edges */}
            <TornEdge top={true} />
            <TornEdge top={false} />
            {/* Content wrapper - above texture */}
            <Box position="relative" zIndex={1} p={5}>
              <Text 
                fontSize="xs" 
                fontWeight="semibold"
                fontStyle="italic"
                textAlign="center"
                mb={3}
                fontFamily="'Montserrat', sans-serif"
              >
                Receipt
              </Text>
              
              <VStack align="stretch" gap={2} fontSize="xs">
              {/* Amount Row */}
              <VStack align="stretch" gap={1}>
                <HStack justify="space-between" align="flex-start" gap={1}>
                  <Text 
                    fontWeight="normal"
                    fontStyle="italic"
                    fontFamily="'Montserrat', sans-serif"
                    whiteSpace="nowrap"
                  >
                    Amount
                  </Text>
                  <VStack align="flex-end" gap={0}>
                    <Text 
                      fontWeight="semibold"
                      fontStyle="italic"
                      fontFamily="'Montserrat', sans-serif"
                      whiteSpace="nowrap"
                    >
                      {formatCurrency(receiptData.amount)}
                    </Text>
                    {receiptData.monthly && (
                      <Text 
                        fontSize="2xs" 
                        fontWeight="normal"
                        fontFamily="'Montserrat', sans-serif"
                        textAlign="right"
                        lineHeight="1.4"
                      >
                        Per Month
                      </Text>
                    )}
                  </VStack>
                </HStack>
              </VStack>

              {/* Cover Fee Row */}
              {receiptData.coverCost && (
                <VStack align="stretch" gap={1}>
                  <HStack justify="space-between" align="flex-start" gap={1}>
                    <Text 
                      fontStyle="italic" 
                      fontWeight="normal"
                      fontFamily="'Montserrat', sans-serif"
                      whiteSpace="nowrap"
                    >
                      Cover Fee
                    </Text>
                    <Text 
                      fontWeight="semibold"
                      fontStyle="italic"
                      fontFamily="'Montserrat', sans-serif"
                      whiteSpace="nowrap"
                    >
                      {formatCurrency(receiptData.coverCostAmount)}
                    </Text>
                  </HStack>
                  <Text 
                    fontSize="2xs" 
                    fontStyle="italic"
                    fontFamily="'Montserrat', sans-serif"
                    textAlign="left"
                    lineHeight="1.4"
                  >
                    Ensures 100% of your donation goes to KUOW!
                  </Text>
                </VStack>
              )}

              {/* Dashed Separator */}
              <Box h={3} display="flex" alignItems="center" justifyContent="center">
                <Box w="full" h="0" borderTopWidth="1px" borderTopStyle="dashed" borderColor="gray.muted" />
              </Box>

              {/* Total Row and Processing Message */}
              <VStack align="stretch" gap={1}>
                <HStack justify="space-between" align="flex-start" gap={1}>
                  <Text 
                    fontWeight="normal"
                    fontStyle="italic"
                    fontFamily="'Montserrat', sans-serif"
                    whiteSpace="nowrap"
                  >
                    Total
                  </Text>
                  <Text 
                    fontWeight="bold"
                    fontStyle="italic"
                    fontFamily="'Montserrat', sans-serif"
                    whiteSpace="nowrap"
                  >
                    {formatCurrency(receiptData.amountWithCoverCost)}
                  </Text>
                </HStack>
              </VStack>
            </VStack>
            </Box>
          </Box>

          {/* Gift Image */}
          {shouldShowGift ? (
            <Box
              borderRadius="md"
              overflow="hidden"
              maxW="200px"
              minW="176px"
              flex="1"
              ml={isWrapped ? 0 : '-10px'}
              zIndex={2}
            >
              <Image src={giftImageUrl} alt="Thank you gift" w="full" h="auto" display="block" />
            </Box>
          ) : null}
        </HStack>

        <VStack gap={1} align="center">
        <Text 
          fontSize="xs" 
          fontWeight="semibold" 
          fontStyle="italic"
          fontFamily="'Montserrat', sans-serif"
          lineHeight="16px"
          textAlign="center"
        >
          Invite others to donate!
        </Text>
          <HStack gap={1} justify="center" align="center">
          <Link
            href={shareLinks.facebook}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Facebook"
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="36px"
            h="36px"
          >
            <Image src={FacebookIcon} alt="" w="9.5625px" h="19.125px" display="block" />
          </Link>
          <Link
            href={shareLinks.x}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on X"
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="36px"
            h="36px"
          >
            <Image src={XIcon} alt="" w="36px" h="36px" display="block" />
          </Link>
          <Link
            href={shareLinks.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on LinkedIn"
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="36px"
            h="36px"
          >
            <Image src={LinkedInIcon} alt="" w="36px" h="36px" display="block" />
          </Link>
          <Link
            href={shareLinks.email}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share via Email"
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="36px"
            h="36px"
          >
            <Image src={EmailIcon} alt="" w="20px" h="13.333px" display="block" />
          </Link>
        </HStack>
      </VStack>
      </VStack>

      {/* C. Matching Challenge Section */}
      <Box bg="bg.mist" borderRadius="8px" px={6} pt={5} pb={4} maxW="360px" mx="auto">
        <VStack gap={4} align="center">
          {/* Icon Graphic */}
          <HStack gap="2px" height="12" align="center" justify="center" pr="3px">
            <Box
              transform="rotate(-4deg)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="36px"
              h="36px"
            >
              <Image src={GiftIcon} alt="" w="36px" h="36px" display="block" />
            </Box>
            <Box w="22px" h="22px" display="flex" alignItems="center" justifyContent="center">
              <Icon as={LuPlus} w="22px" h="22px" color="fg" />
            </Box>
            <Box
              transform="rotate(4deg)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="36px"
              h="36px"
            >
              <Image src={GiftIcon} alt="" w="36px" h="36px" display="block" />
            </Box>
            <Box w="22px" h="22px" display="flex" alignItems="center" justifyContent="center">
              <Icon as={LuEqual} w="22px" h="22px" color="fg" />
            </Box>
            <HStack gap="2px" pr="14px">
              <Box
                transform="rotate(-4deg)"
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="38px"
                h="38px"
              >
                <Image src={GiftIconLarge} alt="" w="38px" h="38px" display="block" />
              </Box>
              <Box
                transform="rotate(4deg)"
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="38px"
                h="38px"
              >
                <Image src={GiftIconLarge} alt="" w="38px" h="38px" display="block" />
              </Box>
            </HStack>
          </HStack>
          
          <VStack gap={1} w="full">
            <Text 
              fontSize="md" 
              fontWeight="bold" 
              fontFamily="'Montserrat', sans-serif"
              lineHeight="24px"
              textAlign="center"
              w="full"
            >
              Double your donation!
            </Text>
            
            <Text 
              fontSize="xs" 
              fontWeight="normal" 
              fontFamily="'Montserrat', sans-serif"
              lineHeight="16px"
              w="full"
            >
              Some employers will match your donation. Find yours below and we can email you details on how to double your donation for free.
            </Text>
          </VStack>

          {dtdSubmitted ? (
            <VStack gap="8px" w="full" py={4}>
              <Text 
                fontSize="sm" 
                fontWeight="normal" 
                fontFamily="'Montserrat', sans-serif"
                lineHeight="20px"
                textAlign="center"
                color="fg.default"
              >
                Thank you, we'll send you additional information.
              </Text>
            </VStack>
          ) : (
            <HStack gap={2} w="full" align="center">
              <Box flex="1">
                <Box id={DTD_CONTAINER_ID} />
              </Box>
              
              <Button
                onClick={() => {
                  void handleDtdSubmit();
                }}
                disabled={!dtdCompany || isSubmittingDtd}
                loading={isSubmittingDtd}
                aria-label="Submit company matching"
                w="10"
                h="10"
                minW="10"
                p={0}
                bg="river.solid"
                color="river.contrast"
                borderRadius="l2"
                _hover={{ bg: 'river.emphasized' }}
                _disabled={{ 
                  bg: 'gray.muted',
                  color: 'gray.fg',
                  cursor: 'not-allowed',
                  opacity: 1,
                }}
              >
                <img src={MailIcon} alt="" style={{ width: '20px', height: '20px', display: 'block' }} />
              </Button>
            </HStack>
          )}
        </VStack>
      </Box>

      {/* D. Why Support KUOW Section */}
      {receiptData.showWhyYouSupportPrompt && (
        <Box bg="bg.overcast" borderRadius="8px" px={6} py={4} maxW="360px" mx="auto">
          <VStack gap={4} align="center">
            <Text 
              fontSize="md" 
              fontWeight="semibold" 
              fontFamily="'Montserrat', sans-serif"
              lineHeight="24px"
              textAlign="center"
              w="full"
            >
              Why do you support KUOW?
            </Text>

            {commentSubmitted ? (
              <VStack gap="8px" w="full" py={4}>
                <Text 
                  fontSize="sm" 
                  fontWeight="normal" 
                  fontFamily="'Montserrat', sans-serif"
                  lineHeight="20px"
                  textAlign="center"
                  color="fg.default"
                >
                  Thank you for sharing!
                </Text>
                <Text 
                  fontSize="xs" 
                  fontWeight="normal" 
                  fontFamily="'Montserrat', sans-serif"
                  lineHeight="16px"
                  textAlign="center"
                  color="fg.default"
                  fontStyle="italic"
                >
                  Your comment may be read on-air or used in other KUOW marketing materials.
                </Text>
              </VStack>
            ) : (
              <>
                <VStack gap={2} w="full" h="32" align="stretch">
                  <Box position="relative" flex="1" w="full">
                    <Textarea
                      value={testimonialText}
                      onChange={(e) => setTestimonialText(e.target.value.slice(0, maxCharacters))}
                      placeholder="Comment (200 character limit)"
                      fontSize="sm"
                      fontFamily="'Montserrat', sans-serif"
                      fontWeight="normal"
                      lineHeight="20px"
                      h="full"
                      py={2}
                      px={3}
                      borderRadius="6px"
                      borderWidth="1px"
                      borderColor="border.muted"
                      bg="bg.panel"
                      resize="none"
                      disabled={isSubmittingComment}
                      css={{
                        '&::placeholder': {
                          color: 'var(--chakra-colors-fg-subtle)'
                        }
                      }}
                    />
                  </Box>
                  <Text 
                    fontSize="xs" 
                    fontWeight="normal" 
                    fontFamily="'Montserrat', sans-serif"
                    lineHeight="16px"
                    color="fg.default"
                    w="full"
                  >
                    Your comments may be read on-air or used in other KUOW marketing materials.
                  </Text>
                </VStack>

                <Button
                  onClick={() => {
                    void handleCommentSubmit();
                  }}
                  disabled={!testimonialText.trim() || isSubmittingComment}
                  loading={isSubmittingComment}
                  h="8"
                  minH="8"
                  minW="8"
                  px={3}
                  py={1}
                  borderWidth="1px"
                  borderStyle="solid"
                  borderRadius="6px"
                  fontSize="xs"
                  fontWeight="medium"
                  fontFamily="'Montserrat', sans-serif"
                  lineHeight="16px"
                  bg="river.solid"
                  color="river.contrast"
                  borderColor="river.solid"
                  _hover={{ bg: 'river.emphasized', borderColor: 'river.emphasized' }}
                  _disabled={{
                    bg: 'transparent',
                    color: 'gray.fg',
                    borderColor: 'gray.muted',
                    cursor: 'not-allowed',
                    opacity: 1,
                    _hover: { bg: 'transparent', borderColor: 'gray.muted' },
                  }}
                >
                  Submit
                </Button>
              </>
            )}
          </VStack>
        </Box>
      )}

      {/* E. Help + Accordion */}
      <VStack gap={4} w="full" align="stretch">
        <VStack gap={2} textAlign="center">
        <Text 
          fontSize="md" 
          fontWeight="medium" 
          fontFamily="'Montserrat', sans-serif"
          lineHeight="24px"
          w="full"
        >
          Need help or have a question?
        </Text>
        <Text 
          fontSize="xs" 
          fontWeight="normal" 
          fontFamily="'Montserrat', sans-serif"
          fontStyle="italic"
          lineHeight="16px"
          w="full"
        >
          Please contact our membership team at{' '}
          <Link 
            href="mailto:membership@kuow.org" 
            textDecoration="underline"
            fontStyle="normal"
            fontFamily="'Outfit', sans-serif"
          >
            membership@kuow.org
          </Link>
          {' '}or call{' '}
          <Link 
            href="tel:+12062212501" 
            textDecoration="underline"
            fontStyle="normal"
            fontFamily="'Outfit', sans-serif"
          >
            (206) 221-2501
          </Link>
          .
        </Text>
      </VStack>

      <VStack gap={0} w="full" align="stretch">
        <Box 
          borderBottomWidth="1px"
          borderBottomColor="border.muted"
          borderBottomStyle="solid"
          px={4}
          py="8px"
        >
          <HStack 
            gap="8px" 
            align="center" 
            px={0} 
            py={2}
            cursor="pointer"
            _hover={{ opacity: 0.8 }}
            onClick={() => toggleAccordion('company-matching')}
          >
            <Box flex="1">
              <Text 
                fontSize="md" 
                fontWeight="medium" 
                fontFamily="'Montserrat', sans-serif"
                lineHeight="24px"
              >
                Company Matching
              </Text>
            </Box>
            <Box 
              w="5" 
              h="5" 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
              transform={expandedAccordions.has('company-matching') ? 'rotate(180deg)' : 'rotate(0deg)'}
              transition="transform 0.2s"
            >
              <Image src={ChevronDownIcon} alt="" w="3" h="2" display="block" />
            </Box>
          </HStack>
          {expandedAccordions.has('company-matching') && (
            <Box px={0} pb={4} pt={2}>
              <Text 
                fontSize="sm" 
                fontWeight="normal" 
                fontFamily="'Montserrat', sans-serif"
                lineHeight="20px"
                color="fg.muted"
              >
                Many employers sponsor matching gift programs and will match charitable contributions made by their employees. To find out if your company has a matching gift policy, contact your Human Resources department.
              </Text>
            </Box>
          )}
        </Box>

        <Box 
          borderBottomWidth="1px"
          borderBottomColor="border.muted"
          borderBottomStyle="solid"
          px={4}
          py="8px"
        >
          <HStack 
            gap="8px" 
            align="center" 
            px={0} 
            py={2}
            cursor="pointer"
            _hover={{ opacity: 0.8 }}
            onClick={() => toggleAccordion('editing-monthly')}
          >
            <Box flex="1">
              <Text 
                fontSize="md" 
                fontWeight="medium" 
                fontFamily="'Montserrat', sans-serif"
                lineHeight="24px"
              >
                Editing Monthly Donation
              </Text>
            </Box>
            <Box 
              w="5" 
              h="5" 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
              transform={expandedAccordions.has('editing-monthly') ? 'rotate(180deg)' : 'rotate(0deg)'}
              transition="transform 0.2s"
            >
              <Image src={ChevronDownIcon} alt="" w="3" h="2" display="block" />
            </Box>
          </HStack>
          {expandedAccordions.has('editing-monthly') && (
            <Box px={0} pb={4} pt={2}>
              <Text 
                fontSize="sm" 
                fontWeight="normal" 
                fontFamily="'Montserrat', sans-serif"
                lineHeight="20px"
                color="fg.muted"
              >
                To edit or cancel your monthly donation, please sign in to your account or request a magic link at{' '}
                <Link
                  href="https://kuow.org/me"
                  textDecoration="underline"
                  fontFamily="'Outfit', sans-serif"
                >
                  kuow.org/me
                </Link>
                .
              </Text>
            </Box>
          )}
        </Box>

        {mode !== 'billing-update' ? (
          <Box 
            borderBottomWidth="1px"
            borderBottomColor="border.muted"
            borderBottomStyle="solid"
            px={4}
            py="8px"
          >
            <HStack 
              gap="8px" 
              align="center" 
              px={0} 
              py={2}
              cursor="pointer"
              _hover={{ opacity: 0.8 }}
              onClick={() => toggleAccordion('gift-shipping')}
            >
              <Box flex="1">
                <Text 
                  fontSize="md" 
                  fontWeight="medium" 
                  fontFamily="'Montserrat', sans-serif"
                  lineHeight="24px"
                >
                  Thank You Gift Shipping
                </Text>
              </Box>
              <Box 
                w="5" 
                h="5" 
                display="flex" 
                alignItems="center" 
                justifyContent="center"
                transform={expandedAccordions.has('gift-shipping') ? 'rotate(180deg)' : 'rotate(0deg)'}
                transition="transform 0.2s"
              >
                <Image src={ChevronDownIcon} alt="" w="3" h="2" display="block" />
              </Box>
            </HStack>
            {expandedAccordions.has('gift-shipping') && (
              <Box px={0} pb={4} pt={2}>
                <Text 
                  fontSize="sm" 
                  fontWeight="normal" 
                  fontFamily="'Montserrat', sans-serif"
                  lineHeight="20px"
                  color="fg.muted"
                >
                  Thank you gifts are typically shipped within 4-6 weeks of your donation. If you have questions about your gift, please contact our membership team at{' '}
                  <Link 
                    href="mailto:membership@kuow.org" 
                    textDecoration="underline"
                    fontFamily="'Outfit', sans-serif"
                  >
                    membership@kuow.org
                  </Link>
                  {' '}or call{' '}
                  <Link 
                    href="tel:+12062212501" 
                    textDecoration="underline"
                    fontFamily="'Outfit', sans-serif"
                  >
                    (206) 221-2501
                  </Link>
                  .
                </Text>
              </Box>
            )}
          </Box>
        ) : null}

        <Box 
          borderBottomWidth="1px"
          borderBottomColor="border.muted"
          borderBottomStyle="solid"
          px={4}
          py="8px"
        >
          <HStack 
            gap="8px" 
            align="center" 
            px={0} 
            py={2}
            cursor="pointer"
            _hover={{ opacity: 0.8 }}
            onClick={() => toggleAccordion('privacy-policy')}
          >
            <Box flex="1">
              <Text 
                fontSize="md" 
                fontWeight="medium" 
                fontFamily="'Montserrat', sans-serif"
                lineHeight="24px"
              >
                Donor Privacy Policy
              </Text>
            </Box>
            <Box 
              w="5" 
              h="5" 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
              transform={expandedAccordions.has('privacy-policy') ? 'rotate(180deg)' : 'rotate(0deg)'}
              transition="transform 0.2s"
            >
              <Image src={ChevronDownIcon} alt="" w="3" h="2" display="block" />
            </Box>
          </HStack>
          {expandedAccordions.has('privacy-policy') && (
            <Box px={0} pb={4} pt={2}>
              <Text 
                fontSize="sm" 
                fontWeight="normal" 
                fontFamily="'Montserrat', sans-serif"
                lineHeight="20px"
                color="fg.muted"
              >
                KUOW respects the privacy of our donors. We do not sell, trade, or share our donors' names or personal information with any other entity. Your contact information is used solely for KUOW purposes and to provide you with information about our programming and fundraising activities.
              </Text>
            </Box>
          )}
        </Box>
      </VStack>
      </VStack>
    </VStack>
  );
};

